import Image from "next/image"
import Link from "next/link"
import { PlusIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"

export interface BiomarkerPackageCard {
  id: string
  /** Product shot shown as the card's icon. */
  image?: string
  label: string
  desc: string
  href: string
}

export interface BiomarkerAddonItem {
  id: string
  label: string
  href: string
}

export interface BiomarkerMembershipCard {
  label: string
  desc: string
  href: string
}

interface BiomarkerPackageCardsProps {
  items: BiomarkerPackageCard[]
  addonItems?: BiomarkerAddonItem[]
  /** Aubergine membership-branded card rendered after the main items. Used
   *  for the blood-followup test which is delivered via Ahead Membership
   *  rather than as a one-off package. */
  membershipCard?: BiomarkerMembershipCard
  /** Heading shown above the card list (e.g. "Included in"). */
  heading: string
  className?: string
}

/**
 * Mega-menu-style "Included in" package cards.
 * Shared between the desktop sticky sidebar and the mobile inline section.
 */
export function BiomarkerPackageCards({
  items,
  addonItems = [],
  membershipCard,
  heading,
  className,
}: BiomarkerPackageCardsProps) {
  if (items.length === 0 && addonItems.length === 0 && !membershipCard)
    return null
  return (
    <div className={className}>
      <h2 className="type-label-lg mb-4 text-neutral-400">{heading}</h2>
      <div className="flex flex-col gap-1 rounded-[16px] bg-white p-2">
        {items.map((pkg) => (
          <Link
            key={pkg.id}
            href={pkg.href}
            className={cn(
              "flex items-center gap-4 rounded-[12px] p-3 transition-colors hover:bg-neutral-50"
            )}
          >
            {pkg.image ? (
              <Image
                src={pkg.image}
                alt=""
                width={52}
                height={52}
                className="h-[52px] w-[52px] shrink-0 rounded-[10px] object-cover"
              />
            ) : null}
            <div className="min-w-0">
              <p className="type-body-lg font-medium text-neutral-900">
                {pkg.label}
              </p>
              <p className="type-body-sm text-neutral-500">{pkg.desc}</p>
            </div>
          </Link>
        ))}
        {addonItems.length > 0 && items.length > 0 && (
          <div className="mx-3 border-t border-neutral-100" />
        )}
        {addonItems.map((addon) => (
          <Link
            key={addon.id}
            href={addon.href}
            className="flex items-center gap-3 rounded-[12px] px-3 py-2.5 transition-colors hover:bg-neutral-50"
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-neutral-100 text-neutral-500">
              <PlusIcon width={16} height={16} />
            </span>
            <p className="type-body-md font-medium text-neutral-900">
              {addon.label}
            </p>
          </Link>
        ))}
        {membershipCard && (
          <>
            {(items.length > 0 || addonItems.length > 0) && (
              <div className="mx-3 border-t border-neutral-100" />
            )}
            <Link
              href={membershipCard.href}
              className="flex items-center gap-4 rounded-[12px] p-3 transition-colors hover:bg-neutral-50"
            >
              <span className="bg-sand-50 flex size-[52px] shrink-0 items-center justify-center rounded-[10px]">
                <Image
                  src="/icons/lab-color-60.svg"
                  alt=""
                  width={28}
                  height={28}
                  className="h-7 w-auto"
                />
              </span>
              <div className="min-w-0">
                <p className="type-body-lg font-medium text-neutral-900">
                  {membershipCard.label}
                </p>
                <p className="type-body-sm text-neutral-500">
                  {membershipCard.desc}
                </p>
              </div>
            </Link>
          </>
        )}
      </div>
    </div>
  )
}
